import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  EnvironmentsBatchAttributeUpdateWithTags,
  EnvironmentsBatchEventUpdateWithTags,
  EnvironmentsBatchUpdateResponse,
  EnvironmentsDescriptionItem,
  EnvironmentsUpdateAttributeMetadataRequest,
  EnvironmentsUpdateAttributeTagsRequest,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import {
  type MetadataPatch,
  invalidateDataIndexAttributes,
  invalidateDataIndexEvents,
  removeDataIndexAttributeCache,
  setDataIndexAttributeMetadataCache,
  setDataIndexEventAttributePrivacyCache,
  setDataIndexEventMetadataCache,
} from './cache';
import { type AttributeScope } from './entity';

/**
 * Writes behind the data index: descriptions, privacy level, tags, deletion,
 * generated descriptions, and CSV exports.
 *
 * Every attribute write exists in three spellings — profile, object,
 * relationship — served by three endpoint families that differ only in their
 * path. The scope picks the family, so no caller has to remember which of the
 * three it is talking to, and an object write cannot be issued without the
 * object type it belongs to.
 */

function environmentPath(workspaceId: number) {
  return { environment_id: String(workspaceId) };
}

/**
 * The fields a save actually sent, as a cache patch. A key present with an
 * undefined value would overwrite the stored one, and the endpoints answer
 * 202 with no body, so there is nothing to restore it from.
 */
function metadataPatch({
  description,
  privacyLevel,
}: {
  description?: string | null;
  privacyLevel?: number;
}): MetadataPatch {
  const patch: MetadataPatch = {};
  if (description !== undefined) {
    patch.description = description;
  }
  if (privacyLevel !== undefined) {
    patch.privacy_level = privacyLevel;
  }
  return patch;
}

type AttributeMetadataBody = EnvironmentsUpdateAttributeMetadataRequest;
type AttributeTagsBody = EnvironmentsUpdateAttributeTagsRequest;
type BatchAttributeUpdate = EnvironmentsBatchAttributeUpdateWithTags;

/**
 * The endpoints that address one attribute, resolved for its scope.
 *
 * Profile, object and relationship attributes are three endpoint families
 * that differ only in their path, so the scope is read once here rather than
 * switched on again in every write.
 */
function attributeEndpoints(workspaceId: number, scope: AttributeScope, name: string) {
  const path = environmentPath(workspaceId);

  switch (scope.kind) {
    case 'profile': {
      const params = { path: { ...path, attribute: name } };
      return {
        writeMetadata: (body: AttributeMetadataBody) => hydraUiApi.attributes.metadata({ params, body }),
        writeTags: (body: AttributeTagsBody) => hydraUiApi.attributes.tags({ params, body }),
        remove: () => hydraUiApi.attributes.delete({ params }),
      };
    }
    case 'object': {
      const params = { path: { ...path, type_id: String(scope.objectTypeId), attribute: name } };
      return {
        writeMetadata: (body: AttributeMetadataBody) => hydraUiApi.objectAttributes.metadata({ params, body }),
        writeTags: (body: AttributeTagsBody) => hydraUiApi.objectAttributes.tags({ params, body }),
        remove: () => hydraUiApi.objectAttributes.delete({ params }),
      };
    }
    case 'relationship': {
      const params = { path: { ...path, type_id: String(scope.objectTypeId), attribute: name } };
      const endpoints = hydraUiApi.relationshipAttributes;
      return {
        writeMetadata: (body: AttributeMetadataBody) =>
          endpoints.objectRelationshipAttributesMetadata({ params, body }),
        writeTags: (body: AttributeTagsBody) => endpoints.objectRelationshipAttributesTags({ params, body }),
        remove: () => endpoints.deleteObjectRelationshipAttributes({ params }),
      };
    }
    default: {
      const _exhaustive: never = scope;
      throw new Error(`unhandled attribute scope: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

/** The batch endpoint for a scope, which addresses every selected attribute at once. */
function batchMetadataEndpoint(workspaceId: number, scope: AttributeScope) {
  const path = environmentPath(workspaceId);

  switch (scope.kind) {
    case 'profile':
      return (attributes: BatchAttributeUpdate[]) =>
        hydraUiApi.attributes.metadataBatch({ params: { path }, body: { attributes } });
    case 'object':
      return (attributes: BatchAttributeUpdate[]) =>
        hydraUiApi.objectAttributes.metadataBatch({
          params: { path: { ...path, type_id: String(scope.objectTypeId) } },
          body: { attributes },
        });
    case 'relationship':
      return (attributes: BatchAttributeUpdate[]) =>
        hydraUiApi.relationshipAttributes.objectRelationshipAttributesMetadataBatch({
          params: { path: { ...path, type_id: String(scope.objectTypeId) } },
          body: { attributes },
        });
    default: {
      const _exhaustive: never = scope;
      throw new Error(`unhandled attribute scope: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

/* ------------------------------------------------------------------------- */
/* Metadata                                                                   */
/* ------------------------------------------------------------------------- */

/** The description and privacy level a save can change. */
export type AttributeMetadataInput = {
  workspaceId: number;
  scope: AttributeScope;
  name: string;
  description?: string | null;
  /** 0 = not sensitive, 1 = sensitive. Omitted leaves the level alone. */
  privacyLevel?: number;
};

/** @public */
export async function updateDataIndexAttributeMetadata({
  workspaceId,
  scope,
  name,
  description,
  privacyLevel,
}: AttributeMetadataInput): Promise<void> {
  const body: AttributeMetadataBody = {};
  if (description !== undefined) {
    body.description = description;
  }
  if (privacyLevel !== undefined) {
    body.privacy_level = privacyLevel;
  }
  await attributeEndpoints(workspaceId, scope, name).writeMetadata(body);
}

export type UseAttributeMetadataVariables = Omit<AttributeMetadataInput, 'workspaceId'>;

type DataIndexMutationOptions<TData, TVariables> = {
  workspaceId: number;
  mutationConfig?: HookMutationConfig<TData, TVariables>;
};

/** @public */
export const useUpdateDataIndexAttributeMetadata = ({
  workspaceId,
  mutationConfig,
}: DataIndexMutationOptions<void, UseAttributeMetadataVariables>) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseAttributeMetadataVariables) => updateDataIndexAttributeMetadata({ ...input, workspaceId }),
    onSuccess: (result, variables, ...args) => {
      setDataIndexAttributeMetadataCache(
        queryClient,
        workspaceId,
        variables.scope,
        variables.name,
        metadataPatch(variables),
      );
      void invalidateDataIndexAttributes(queryClient, workspaceId);
      onSuccess?.(result, variables, ...args);
    },
    ...rest,
  });
};

export type EventMetadataInput = {
  workspaceId: number;
  name: string;
  description?: string | null;
};

/** @public */
export async function updateDataIndexEventMetadata({
  workspaceId,
  name,
  description,
}: EventMetadataInput): Promise<void> {
  await hydraUiApi.events.eventNamesMetadata({
    params: { path: { ...environmentPath(workspaceId), event: name } },
    body: { description },
  });
}

export type UseEventMetadataVariables = Omit<EventMetadataInput, 'workspaceId'>;

/** @public */
export const useUpdateDataIndexEventMetadata = ({
  workspaceId,
  mutationConfig,
}: DataIndexMutationOptions<void, UseEventMetadataVariables>) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseEventMetadataVariables) => updateDataIndexEventMetadata({ ...input, workspaceId }),
    onSuccess: (result, variables, ...args) => {
      setDataIndexEventMetadataCache(queryClient, workspaceId, variables.name, metadataPatch(variables));
      void invalidateDataIndexEvents(queryClient, workspaceId);
      onSuccess?.(result, variables, ...args);
    },
    ...rest,
  });
};

/* ------------------------------------------------------------------------- */
/* Tags                                                                       */
/* ------------------------------------------------------------------------- */

export type AttributeTagsInput = {
  workspaceId: number;
  scope: AttributeScope;
  name: string;
  tagIds: number[];
};

/** @public */
export async function updateDataIndexAttributeTags({
  workspaceId,
  scope,
  name,
  tagIds,
}: AttributeTagsInput): Promise<void> {
  await attributeEndpoints(workspaceId, scope, name).writeTags({ tag_ids: tagIds });
}

export type UseAttributeTagsVariables = Omit<AttributeTagsInput, 'workspaceId'>;

/** @public */
export const useUpdateDataIndexAttributeTags = ({
  workspaceId,
  mutationConfig,
}: DataIndexMutationOptions<void, UseAttributeTagsVariables>) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseAttributeTagsVariables) => updateDataIndexAttributeTags({ ...input, workspaceId }),
    onSuccess: (result, variables, ...args) => {
      setDataIndexAttributeMetadataCache(queryClient, workspaceId, variables.scope, variables.name, {
        tag_ids: variables.tagIds,
      });
      void invalidateDataIndexAttributes(queryClient, workspaceId);
      onSuccess?.(result, variables, ...args);
    },
    ...rest,
  });
};

export type EventTagsInput = {
  workspaceId: number;
  name: string;
  tagIds: number[];
};

/** @public */
export async function updateDataIndexEventTags({ workspaceId, name, tagIds }: EventTagsInput): Promise<void> {
  await hydraUiApi.events.eventNamesTags({
    params: { path: { ...environmentPath(workspaceId), event: name } },
    body: { tag_ids: tagIds },
  });
}

export type UseEventTagsVariables = Omit<EventTagsInput, 'workspaceId'>;

/** @public */
export const useUpdateDataIndexEventTags = ({
  workspaceId,
  mutationConfig,
}: DataIndexMutationOptions<void, UseEventTagsVariables>) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseEventTagsVariables) => updateDataIndexEventTags({ ...input, workspaceId }),
    onSuccess: (result, variables, ...args) => {
      setDataIndexEventMetadataCache(queryClient, workspaceId, variables.name, { tag_ids: variables.tagIds });
      void invalidateDataIndexEvents(queryClient, workspaceId);
      onSuccess?.(result, variables, ...args);
    },
    ...rest,
  });
};

/* ------------------------------------------------------------------------- */
/* Batch metadata                                                             */
/* ------------------------------------------------------------------------- */

export type BatchAttributeMetadataInput = {
  workspaceId: number;
  scope: AttributeScope;
  attributes: BatchAttributeUpdate[];
};

/** @public */
export async function batchUpdateDataIndexAttributeMetadata({
  workspaceId,
  scope,
  attributes,
}: BatchAttributeMetadataInput): Promise<EnvironmentsBatchUpdateResponse> {
  return batchMetadataEndpoint(workspaceId, scope)(attributes);
}

export type UseBatchAttributeMetadataVariables = Omit<BatchAttributeMetadataInput, 'workspaceId'>;

/** @public */
export const useBatchUpdateDataIndexAttributeMetadata = ({
  workspaceId,
  mutationConfig,
}: DataIndexMutationOptions<EnvironmentsBatchUpdateResponse, UseBatchAttributeMetadataVariables>) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseBatchAttributeMetadataVariables) =>
      batchUpdateDataIndexAttributeMetadata({ ...input, workspaceId }),
    onSuccess: (result, ...args) => {
      void invalidateDataIndexAttributes(queryClient, workspaceId);
      onSuccess?.(result, ...args);
    },
    ...rest,
  });
};

export type BatchEventMetadataInput = {
  workspaceId: number;
  events: EnvironmentsBatchEventUpdateWithTags[];
};

/** @public */
export async function batchUpdateDataIndexEventMetadata({
  workspaceId,
  events,
}: BatchEventMetadataInput): Promise<EnvironmentsBatchUpdateResponse> {
  return hydraUiApi.events.eventNamesMetadataBatch({
    params: { path: environmentPath(workspaceId) },
    body: { events },
  });
}

export type UseBatchEventMetadataVariables = Omit<BatchEventMetadataInput, 'workspaceId'>;

/** @public */
export const useBatchUpdateDataIndexEventMetadata = ({
  workspaceId,
  mutationConfig,
}: DataIndexMutationOptions<EnvironmentsBatchUpdateResponse, UseBatchEventMetadataVariables>) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseBatchEventMetadataVariables) => batchUpdateDataIndexEventMetadata({ ...input, workspaceId }),
    onSuccess: (result, ...args) => {
      void invalidateDataIndexEvents(queryClient, workspaceId);
      onSuccess?.(result, ...args);
    },
    ...rest,
  });
};

/* ------------------------------------------------------------------------- */
/* Deletion                                                                   */
/* ------------------------------------------------------------------------- */

export type DeleteAttributeInput = {
  workspaceId: number;
  scope: AttributeScope;
  name: string;
};

/**
 * Schedule one attribute for deletion. The work happens in the background, so
 * a success here means the request was accepted, not that the attribute is
 * already gone.
 *
 * @public
 */
export async function deleteDataIndexAttribute({ workspaceId, scope, name }: DeleteAttributeInput): Promise<void> {
  await attributeEndpoints(workspaceId, scope, name).remove();
}

export type UseDeleteAttributeVariables = Omit<DeleteAttributeInput, 'workspaceId'>;

/** @public */
export const useDeleteDataIndexAttribute = ({
  workspaceId,
  mutationConfig,
}: DataIndexMutationOptions<void, UseDeleteAttributeVariables>) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseDeleteAttributeVariables) => deleteDataIndexAttribute({ ...input, workspaceId }),
    onSuccess: (result, variables, ...args) => {
      removeDataIndexAttributeCache(queryClient, workspaceId, variables.scope, variables.name);
      void invalidateDataIndexAttributes(queryClient, workspaceId);
      onSuccess?.(result, variables, ...args);
    },
    ...rest,
  });
};

export type DeleteAttributesInput = {
  workspaceId: number;
  names: string[];
};

/**
 * Schedule several profile attributes for deletion in one request. Only
 * profile attributes have a batch endpoint; object and relationship
 * attributes are deleted one at a time.
 *
 * @public
 */
export async function deleteDataIndexAttributes({ workspaceId, names }: DeleteAttributesInput): Promise<void> {
  await hydraUiApi.attributes.deleteMany({
    params: { path: environmentPath(workspaceId) },
    body: { attributes: names },
  });
}

export type UseDeleteAttributesVariables = Omit<DeleteAttributesInput, 'workspaceId'>;

/** @public */
export const useDeleteDataIndexAttributes = ({
  workspaceId,
  mutationConfig,
}: DataIndexMutationOptions<void, UseDeleteAttributesVariables>) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseDeleteAttributesVariables) => deleteDataIndexAttributes({ ...input, workspaceId }),
    onSuccess: (result, variables, ...args) => {
      for (const name of variables.names) {
        removeDataIndexAttributeCache(queryClient, workspaceId, { kind: 'profile' }, name);
      }
      void invalidateDataIndexAttributes(queryClient, workspaceId);
      onSuccess?.(result, variables, ...args);
    },
    ...rest,
  });
};

/* ------------------------------------------------------------------------- */
/* Generated descriptions                                                     */
/* ------------------------------------------------------------------------- */

export type GenerateDescriptionsInput = {
  workspaceId: number;
  type: 'attribute' | 'event';
  items: string[];
  objectTypeId?: number;
};

/**
 * Ask the model for descriptions of up to 50 attributes or events. The
 * descriptions come back for the caller to review; nothing is saved until a
 * metadata write follows.
 *
 * @public
 */
export async function generateDataIndexDescriptions({
  workspaceId,
  type,
  items,
  objectTypeId,
}: GenerateDescriptionsInput): Promise<EnvironmentsDescriptionItem[]> {
  const raw = await hydraUiApi.dataIndex.generateDescriptions({
    params: { path: environmentPath(workspaceId) },
    body: { type, items, object_type_id: objectTypeId },
  });
  return raw.descriptions;
}

export type UseGenerateDescriptionsVariables = Omit<GenerateDescriptionsInput, 'workspaceId'>;

/** @public */
export const useGenerateDataIndexDescriptions = ({
  workspaceId,
  mutationConfig,
}: DataIndexMutationOptions<EnvironmentsDescriptionItem[], UseGenerateDescriptionsVariables>) =>
  useMutation({
    mutationFn: (input: UseGenerateDescriptionsVariables) => generateDataIndexDescriptions({ ...input, workspaceId }),
    ...mutationConfig,
  });

/* ------------------------------------------------------------------------- */
/* Event attribute privacy                                                    */
/* ------------------------------------------------------------------------- */

export type EventAttributePrivacyInput = {
  workspaceId: number;
  eventName: string;
  attributeName: string;
  /** 0 = not sensitive, 1 = sensitive. */
  privacyLevel: number;
};

/**
 * Mark one of an event's own attributes sensitive, or not.
 *
 * The endpoint takes a batch, and this sends one: an event's attributes are
 * marked one at a time from the table that lists them.
 *
 * @public
 */
export async function updateDataIndexEventAttributePrivacy({
  workspaceId,
  eventName,
  attributeName,
  privacyLevel,
}: EventAttributePrivacyInput): Promise<void> {
  await hydraUiApi.events.eventAttributesMetadataBatch({
    params: { path: { ...environmentPath(workspaceId), event: eventName } },
    body: { attributes: [{ name: attributeName, privacy_level: privacyLevel }] },
  });
}

export type UseEventAttributePrivacyVariables = Omit<EventAttributePrivacyInput, 'workspaceId'>;

/** @public */
export const useUpdateDataIndexEventAttributePrivacy = ({
  workspaceId,
  mutationConfig,
}: DataIndexMutationOptions<void, UseEventAttributePrivacyVariables>) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseEventAttributePrivacyVariables) =>
      updateDataIndexEventAttributePrivacy({ ...input, workspaceId }),
    onSuccess: (result, variables, ...args) => {
      setDataIndexEventAttributePrivacyCache(
        queryClient,
        workspaceId,
        variables.eventName,
        variables.attributeName,
        variables.privacyLevel,
      );
      onSuccess?.(result, variables, ...args);
    },
    ...rest,
  });
};
